import type HexCoords from '../grid/HexCoords';
import type { Bearing } from '../grid/HexBearing';
import type { AnimationStep } from './Action';

export enum ProposalType {
  REPLACEMENT = 'REPLACEMENT',
  AUGMENT = 'AUGMENT',
}

/**
 * Attack timing determines when an attack resolves during tick processing.
 *
 * ## Phase 1 Timing (Movement Resolution Phase)
 * - **START**: Attack resolves against initial position only (before movement)
 * - **CONFLICT**: Attack only triggers during movement conflicts
 *
 * ## Phase 2 Timing (Post-Movement Combat Phase)
 * - **END**: Attack resolves against final position only (after movement)
 * - **BOTH**: Attack only hits if target was stationary (same initial/final position)
 * - **ANY**: Attack attempts at initial position first; if miss, retries at final position
 *
 * @see Phase2aAttackEffect for START/ANY/CONFLICT timing
 * @see Phase2bAttackEffect for END/BOTH timing
 */
export type AttackTiming = 'START' | 'END' | 'ANY' | 'BOTH' | 'CONFLICT';

/**
 * Phase 2a Attack Effect: START, ANY, or CONFLICT timing.
 *
 * These attacks can safely include any effects (including MOVE) in their onMiss handlers
 * because they resolve during Phase 1 (movement resolution), before final positions
 * are determined.
 *
 * ## Timing Semantics:
 *
 * ### START Timing
 * - Resolves in Phase 1 against the target's **initial position**
 * - If target moves away, triggers onMiss effects
 * - Use case: Attacks that represent "as they leave" strikes
 * - Example: Opportunity attacks against fleeing enemies
 *
 * ### ANY Timing
 * - First attempts in Phase 1 at initial position
 * - If missed in Phase 1, retries in Phase 2 at final position
 * - Only triggers onMiss if missed both attempts
 * - Use case: Flexible attacks that adapt to target movement
 * - Example: Wide sweeping attacks that hit whether target stays or moves
 *
 * ### CONFLICT Timing
 * - Only triggers when the attacker is involved in a movement conflict
 * - Targets the conflict participant (stationary defender or collision opponent)
 * - Does not trigger onMiss (conflict resolution handles failures)
 * - Use case: Attacks that represent collision damage
 * - Example: Charging attacks, ram attacks
 *
 * @example
 * ```typescript
 * const lungeAttack: Phase2aAttackEffect = {
 *   type: 'ATTACK',
 *   attackTiming: 'START',
 *   attackHex: targetHex,
 *   damage: 15,
 *   // Safe: Can have MOVE in onMiss because START timing resolves in Phase 1
 *   onMiss: [{
 *     type: 'MOVE',
 *     coords: targetHex,
 *     animationPlan: [[{ type: 'LUNGE', entityId: actor.id }]]
 *   }]
 * };
 * ```
 */
export interface Phase2aAttackEffect {
  type: 'ATTACK';
  attackTiming: 'START' | 'ANY' | 'CONFLICT';
  attackHex: HexCoords;
  damage: number;
  /** Effects to apply if attack misses (can include any effect type) */
  onMiss?: AnyEffect[];
  /** Animation if attack is blocked */
  onBlocked?: {
    animationPlan?: AnimationStep[][];
  };
  /** Animation for the attack */
  animationPlan?: AnimationStep[][];
  /** How to merge animation with existing plan */
  animationMergeStrategy?: 'REPLACE' | 'APPEND';
}

/**
 * Phase 2b Attack Effect: END or BOTH timing.
 *
 * These attacks resolve in Phase 2 (post-movement), after all entities have moved
 * to their final positions. Per ARR-1a (Action Resolution Rule 1a), they CANNOT
 * include MoveEffect in onMiss handlers because movement is already finalized.
 *
 * ## Timing Semantics:
 *
 * ### END Timing
 * - Resolves in Phase 2 against the target's **final position**
 * - Use case: Attacks representing "where they land" strikes
 * - Example: Standing guard at a position, hitting anyone who moves there
 * - Example: Ranged attacks fired at target's destination
 *
 * ### BOTH Timing
 * - Requires target to be at attackHex in **both** initial and final positions
 * - Only hits stationary targets; moving targets automatically dodge
 * - Use case: Attacks requiring a stationary target (e.g., aimed shots, channeled attacks)
 * - Example: Sniper shot that only hits if enemy doesn't move
 * - Example: Ground-based AoE that damages entities standing in it
 *
 * ## ARR-1a Restriction
 * The onMiss handler cannot contain MoveEffect because:
 * 1. Movement phase (Phase 1) is already complete
 * 2. ProjectedOccupancy is finalized and locked
 * 3. Moving entities after Phase 1 would break system invariants
 * 4. All spatial conflicts have been resolved
 *
 * Allowed in onMiss: MissEffect (for animations)
 * Forbidden in onMiss: MoveEffect, TurnEffect, nested AttackEffect
 *
 * @example
 * ```typescript
 * const forwardBite: Phase2bAttackEffect = {
 *   type: 'ATTACK',
 *   attackTiming: 'BOTH',
 *   attackHex: forwardHex,
 *   damage: 10,
 *   // ARR-1a compliant: Only MISS effect, no MOVE
 *   onMiss: [{
 *     type: 'MISS',
 *     target: target,
 *     animationPlan: [[{ type: 'ATTACK_JAB', entityId: actor.id }]]
 *   }]
 * };
 * ```
 *
 * @example
 * ```typescript
 * const rangedAttack: Phase2bAttackEffect = {
 *   type: 'ATTACK',
 *   attackTiming: 'END',
 *   attackHex: targetFinalPosition,
 *   damage: 20,
 *   animationPlan: [[{
 *     type: 'PROJECTILE',
 *     entityId: archer.id,
 *     targetCoords: targetFinalPosition
 *   }]]
 * };
 * ```
 */
export interface Phase2bAttackEffect {
  type: 'ATTACK';
  attackTiming: 'END' | 'BOTH';
  attackHex: HexCoords;
  damage: number;
  /**
   * Effects to apply if attack misses.
   *
   * **ARR-1a**: Cannot contain MoveEffect. Movement resolution is complete
   * by Phase 2, and moving here would violate system invariants.
   *
   * Allowed effects: MISS (for animations)
   * Forbidden effects: MOVE, TURN, ATTACK (nested attacks not supported)
   */
  onMiss?: MissEffect[];
  /** Animation if attack is blocked */
  onBlocked?: {
    animationPlan?: AnimationStep[][];
  };
  /** Animation for the attack */
  animationPlan?: AnimationStep[][];
  /** How to merge animation with existing plan */
  animationMergeStrategy?: 'REPLACE' | 'APPEND';
}

/**
 * Union of all valid attack effects.
 *
 * TypeScript will enforce:
 * - Phase 2a (START/ANY/CONFLICT): onMiss can be AnyEffect[]
 * - Phase 2b (END/BOTH): onMiss must be MissEffect[]
 *
 * This prevents violations of ARR-1a at compile time.
 */
export type AttackEffect = Phase2aAttackEffect | Phase2bAttackEffect;

export interface BlockEffect {
  type: 'BLOCK';
  fromBearings: number[]; // relative bearings
}

export interface MoveEffect {
  type: 'MOVE';
  coords: HexCoords;
  animationPlan?: AnimationStep[][];
}

export interface TurnEffect {
  type: 'TURN';
  bearing: Bearing;
  animationPlan?: AnimationStep[][];
}

export interface IdleEffect {
  type: 'IDLE';
}

export interface MissEffect {
  type: 'MISS';
  target?: any;
  animationPlan?: AnimationStep[][];
}

// Add other effect types here in the future
export type AnyEffect = AttackEffect | BlockEffect | MoveEffect | TurnEffect | IdleEffect | MissEffect;

export interface Proposal {
  type: ProposalType;
  priority: number;
  effects: AnyEffect[];
}
